import { BranchResolveRequest } from "../Shared/Generated/BranchResolveRequest";
import { BranchResolveResponse } from "../Shared/Generated/BranchResolveResponse";
import { BranchRecoveryDiagnostics } from "../Shared/Generated/BranchRecoveryDiagnostics";
import { BranchRecoveryCode } from "../Shared/Generated/BranchRecoveryCode";
import { BranchFailurePhase } from "../Shared/Generated/BranchFailurePhase";
import { BranchSdkErrorType } from "../Shared/Generated/BranchSdkErrorType";
import { BranchResolverRequestDiagnostic } from "../Shared/Generated/BranchResolverRequestDiagnostic";
import { BranchResolverRequestOutcome } from "../Shared/Generated/BranchResolverRequestOutcome";
import { BranchPageVisibility } from "../Shared/Generated/BranchPageVisibility";
import { boundedDuration, clientErrorType, httpStatus } from "./BranchDiagnostics";

const visibility = () => document.visibilityState === "hidden" ? BranchPageVisibility.Hidden :
    document.visibilityState === "visible" ? BranchPageVisibility.Visible : BranchPageVisibility.Unknown;
const safeId = (value: unknown): string | null => typeof value === "string" && /^[a-f0-9]{32}$/i.test(value) ? value : null;

export class BranchResolveRequestError extends Error {
    constructor(readonly reason: BranchRecoveryCode, readonly errorType: BranchSdkErrorType) { super(reason); }
}

export async function resolveBranchRequest(request: BranchResolveRequest, csrf: string, attemptId: string,
    signal: AbortSignal, diagnostics: BranchRecoveryDiagnostics, attemptStartedAt = performance.now()): Promise<BranchResolveResponse> {
    const body = JSON.stringify(request);
    const headers = { "Content-Type": "application/json", RequestVerificationToken: csrf, "X-Branch-Attempt-Id": attemptId };
    for (let requestNumber = 0; ; requestNumber++) {
        if (signal.aborted) throw new DOMException("Resolution canceled", "AbortError");
        const current = new AbortController();
        const startedAt = performance.now();
        const entry: BranchResolverRequestDiagnostic = { requestNumber: requestNumber + 1,
            startedAfterMs: boundedDuration(startedAt - attemptStartedAt), elapsedMs: 0,
            headersMs: null, bodyMs: null, httpStatus: null, outcome: BranchResolverRequestOutcome.InFlight,
            errorType: BranchSdkErrorType.None, serverRequestId: null, correlationId: null,
            visibilityAtStart: visibility(), visibilityAtEnd: visibility(), wasHidden: document.visibilityState === "hidden" };
        (diagnostics.resolveRequests ??= []).push(entry);
        let completed = false;
        let bodyStartedAt: number | undefined;
        const hidden = () => { if (document.visibilityState === "hidden") entry.wasHidden = true; };
        document.addEventListener("visibilitychange", hidden);
        const finish = (outcome: BranchResolverRequestOutcome, errorType = BranchSdkErrorType.None) => {
            if (completed) return;
            completed = true;
            const finishedAt = performance.now();
            entry.elapsedMs = boundedDuration(finishedAt - startedAt);
            entry.bodyMs = bodyStartedAt === undefined ? null : boundedDuration(finishedAt - bodyStartedAt);
            entry.outcome = outcome;
            entry.errorType = errorType;
            entry.visibilityAtEnd = visibility();
            hidden();
            diagnostics.wasHidden ||= entry.wasHidden;
        };
        let timedOut = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let cancel = () => {};
        const canceled = new Promise<never>((_, reject) => {
            cancel = () => {
                finish(signal.reason === "Deadline" ? BranchResolverRequestOutcome.OverallDeadline : BranchResolverRequestOutcome.Canceled, BranchSdkErrorType.AbortError);
                reject(new DOMException("Resolution canceled", "AbortError")); current.abort();
            };
            signal.addEventListener("abort", cancel, { once: true });
            timer = setTimeout(() => {
                timedOut = true;
                finish(BranchResolverRequestOutcome.RequestTimeout, BranchSdkErrorType.TimeoutError);
                reject(new DOMException("Resolver request timed out", "TimeoutError"));
                current.abort();
            }, 6000);
        });
        diagnostics.phase = BranchFailurePhase.ResolveRequest;
        diagnostics.resolveHttpStatus = null;
        diagnostics.resolveRequestCount = requestNumber + 1;
        let reason = BranchRecoveryCode.ResolveNetworkError;
        const read = async (): Promise<BranchResolveResponse> => {
            const response = await fetch("/branch/resolve", { method: "POST", credentials: "same-origin", signal: current.signal,
                headers: { ...headers, "X-Branch-Request-Number": String(requestNumber + 1) }, body });
            if (current.signal.aborted) throw new DOMException("Resolution canceled", "AbortError");
            entry.headersMs = boundedDuration(performance.now() - startedAt);
            entry.httpStatus = httpStatus(response.status);
            entry.serverRequestId = safeId(response.headers?.get("X-Branch-Request-Id"));
            diagnostics.resolveHttpStatus = httpStatus(response.status);
            reason = BranchRecoveryCode.ResolveHttpError;
            if (!response.ok) throw new Error("Resolution failed");
            diagnostics.phase = BranchFailurePhase.ResolveResponse;
            reason = BranchRecoveryCode.ResolveResponseInvalid;
            bodyStartedAt = performance.now();
            const result = await response.json();
            if (current.signal.aborted) throw new DOMException("Resolution canceled", "AbortError");
            entry.correlationId = safeId(result?.correlationId);
            return result;
        };
        try {
            const result = await Promise.race([read(), canceled]);
            finish(BranchResolverRequestOutcome.Completed);
            return result;
        } catch (error) {
            if (signal.aborted) throw error;
            const networkError = error instanceof TypeError;
            finish(timedOut ? BranchResolverRequestOutcome.RequestTimeout : networkError ? BranchResolverRequestOutcome.NetworkError :
                entry.httpStatus !== null && entry.httpStatus >= 400 ? BranchResolverRequestOutcome.HttpError : BranchResolverRequestOutcome.InvalidResponse,
                timedOut ? BranchSdkErrorType.TimeoutError : clientErrorType(error));
            if (requestNumber === 0 && (timedOut || networkError)) continue;
            throw new BranchResolveRequestError(timedOut ? BranchRecoveryCode.ResolveTimeout :
                networkError ? BranchRecoveryCode.ResolveNetworkError : reason,
                timedOut ? BranchSdkErrorType.TimeoutError : clientErrorType(error));
        } finally {
            clearTimeout(timer);
            signal.removeEventListener("abort", cancel);
            document.removeEventListener("visibilitychange", hidden);
            current.abort();
        }
    }
}
